import { useEffect, useState } from "react";

import {
  ApiKeysService,
  type ApiKeyDetail,
  type UpdateApiKeyRequest,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface ApiKeyEditDialogProps {
  apiKey: ApiKeyDetail | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Update — name + description ONLY. The backend rejects attempts
 * to change scope / rate-limit / expiry on PATCH; industry pattern
 * (Anthropic / OpenAI) is to delete and recreate the key.
 */
export function ApiKeyEditDialog({
  apiKey,
  onClose,
  onSuccess,
}: ApiKeyEditDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (apiKey) {
      setName(apiKey.name);
      setDescription(apiKey.description ?? "");
    }
  }, [apiKey]);

  const { mutate, isLoading, error, reset } = useMutation<
    UpdateApiKeyRequest,
    ApiKeyDetail
  >((body) => ApiKeysService.update(apiKey!.id, body), {
    onSuccess: () => {
      onSuccess();
    },
  });

  if (!apiKey) return null;

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const body: UpdateApiKeyRequest = {};
    if (trimmedName !== apiKey.name) body.name = trimmedName;
    const trimmedDesc = description.trim();
    if (trimmedDesc !== (apiKey.description ?? "")) {
      body.description = trimmedDesc || null;
    }
    if (Object.keys(body).length === 0) {
      handleClose();
      return;
    }
    await mutate(body);
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("apiKeys.editDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("apiKeys.editDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <FormField
            label={t("apiKeys.editDialog.nameLabel")}
            value={name}
            onChange={setName}
            maxLength={255}
            required
            autoFocus
          />
          <FormField
            label={t("apiKeys.editDialog.descriptionLabel")}
            value={description}
            onChange={setDescription}
            placeholder={t("apiKeys.editDialog.descriptionPlaceholder")}
            maxLength={500}
          />
          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("apiKeys.editDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading || !name.trim()}>
            {isLoading
              ? t("apiKeys.editDialog.submitting")
              : t("apiKeys.editDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
